
      import '@folkjs/labs/standalone/folk-shape';
      import '@folkjs/labs/standalone/folk-spectrogram';
      import { FolkRTC } from '@folkjs/labs/FolkRTC';
      import { GGWave } from '@folkjs/labs/ggwave';

      // DOM Elements
      const connectionStateIndicator = document.getElementById('connectionStateIndicator');
      const createOfferBtn = document.getElementById('createOfferBtn');
      const listenBtn = document.getElementById('listenBtn');
      const modeToggle = document.getElementById('modeToggle');
      const connectionLog = document.getElementById('connectionLog');
      const chatContainer = document.getElementById('chatContainer');
      const testMessageInput = document.getElementById('testMessageInput');
      const sendTestBtn = document.getElementById('sendTestBtn');
      const spectrogram = document.getElementById('spectrogram');

      // Core state
      let rtcConnection = null;
      let clientId = generateClientId();
      let wave = null;
      let isUltrasonic = false;

      // Connection states
      const CONNECTION_STATES = {
        WAITING: 'waiting',
        CONNECTING: 'connecting',
        CONNECTED: 'connected',
        FAILED: 'failed',
      };

      // Generate a unique client ID
      function generateClientId() {
        return Math.random().toString(36).substring(2, 7);
      }

      // Log function
      function log(message, type = 'system') {
        const entry = document.createElement('div');
        entry.className = `log-entry ${type}`;
        entry.textContent = message;
        connectionLog.appendChild(entry);
        connectionLog.scrollTop = connectionLog.scrollHeight;
        console.log(`[${type}] ${message}`);
      }

      // Update connection state
      function updateConnectionState(state) {
        connectionStateIndicator.textContent = state;
        connectionStateIndicator.className = `connection-state state-${state}`;
      }

      // Add message to chat container
      function addChatMessage(message, senderId) {
        const entry = document.createElement('div');
        entry.className = `chat-message ${senderId === 'self' ? 'sent' : 'received'}`;
        entry.textContent = message;
        chatContainer.appendChild(entry);
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }

      // Initialize GGWave
      async function initGGWave() {
        wave = new GGWave();
        await wave.ready();

        // Configure iOS audio session to prevent muting
        if (navigator.audioSession) {
          try {
            // iOS 17+ solution
            // navigator.audioSession.type = 'playback';
            log('Set iOS audio session to playback mode', 'system');
          } catch (error) {
            log('Could not set iOS audio session type (older iOS version)', 'system');

            // Fallback for older iOS: play silent audio to keep audio session active
            const silentAudio = new Audio();
            silentAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
            silentAudio.loop = true;

            // Only play after user interaction
            document.addEventListener(
              'touchstart',
              () => {
                silentAudio.play().catch((e) => log('Failed to play silent audio', 'error'));
              },
              { once: true },
            );
          }
        }

        // Set up audio visualization
        wave.setVisualizer((node, context) => {
          if (node) {
            spectrogram.connect(node, context);
            spectrogram.start();
          } else {
            spectrogram.stop();
          }
        });

        // Set initial protocol
        wave.setProtocol(GGWave.GGWAVE_PROTOCOL_AUDIBLE_FASTEST);
      }

      // Create and send WebRTC offer
      async function createAndSendOffer() {
        try {
          // Create RTC connection if it doesn't exist
          if (!rtcConnection) {
            rtcConnection = new FolkRTC();
            setupRTCCallbacks();
          }

          log('Creating WebRTC offer...', 'rtc');
          const encodedOffer = await rtcConnection.createOffer();

          // Send offer over audio
          log(`Sending offer over audio... ${encodedOffer}`, 'audio');
          await wave.send(encodedOffer);
          log('Offer sent, waiting for answer...', 'audio');
          updateConnectionState(CONNECTION_STATES.CONNECTING);
        } catch (error) {
          log(`Error creating offer: ${error.message}`, 'error');
          updateConnectionState(CONNECTION_STATES.FAILED);
        }
      }

      // Handle incoming WebRTC messages over audio
      async function handleAudioMessage(message) {
        try {
          log(`Received audio message: ${message}`, 'audio');
          // Skip our own messages by checking the first character
          // 'o' = offer, 'a' = answer
          const messageType = message.charAt(0);

          if (messageType === 'o') {
            log('Received WebRTC offer', 'audio');

            // Create RTC connection if it doesn't exist
            if (!rtcConnection) {
              rtcConnection = new FolkRTC();
              setupRTCCallbacks();
            }

            // Create and send answer
            log('Creating answer...', 'rtc');
            const encodedAnswer = await rtcConnection.createAnswer(message);

            log('Sending answer over audio...', 'audio');
            await wave.send(encodedAnswer);
            updateConnectionState(CONNECTION_STATES.CONNECTING);
          } else if (messageType === 'a') {
            log('Received WebRTC answer', 'audio');

            if (!rtcConnection) {
              log('No RTC connection exists to handle answer', 'error');
              return;
            }

            // Set the answer to complete the connection
            log('Setting answer...', 'rtc');
            await rtcConnection.setAnswer(message);
            log('Answer set, connection should establish', 'rtc');
          }
        } catch (error) {
          log(`Error handling audio message: ${error.message}`, 'error');
        }
      }

      // Set up RTC callbacks
      function setupRTCCallbacks() {
        rtcConnection.onStatusChange = (status) => {
          log(`RTC status: ${status}`, 'rtc');

          // Update UI based on connection status
          if (status === 'connected') {
            updateConnectionState(CONNECTION_STATES.CONNECTED);
            sendTestBtn.disabled = false;
          } else if (status === 'disconnected' || status === 'failed') {
            updateConnectionState(CONNECTION_STATES.FAILED);
            sendTestBtn.disabled = true;
          }
        };

        rtcConnection.onMessage = (message) => {
          log(`Received RTC message: ${message}`, 'rtc');
          addChatMessage(message, 'peer');
        };
      }

      // Send a test message over RTC
      function sendTestMessage() {
        const message = testMessageInput.value.trim();
        if (!message || !rtcConnection || !rtcConnection.isConnected()) return;

        rtcConnection.sendMessage(message);
        addChatMessage(message, 'self');
        testMessageInput.value = '';
      }

      // Event listeners
      createOfferBtn.addEventListener('click', createAndSendOffer);

      listenBtn.addEventListener('click', async () => {
        const isListening = listenBtn.classList.contains('active');

        if (isListening) {
          wave.stopListening();
          listenBtn.textContent = 'Start Listening';
          listenBtn.classList.remove('active');
        } else {
          listenBtn.textContent = 'Stop Listening';
          listenBtn.classList.add('active');
          await wave.startListening(handleAudioMessage);
          log('Started listening for audio messages', 'audio');
        }
      });

      modeToggle.addEventListener('click', () => {
        isUltrasonic = !isUltrasonic;
        modeToggle.textContent = `Ultrasonic: ${isUltrasonic ? 'On' : 'Off'}`;
        modeToggle.classList.toggle('active', isUltrasonic);
        wave.setProtocol(
          isUltrasonic ? GGWave.GGWAVE_PROTOCOL_ULTRASOUND_FASTEST : GGWave.GGWAVE_PROTOCOL_AUDIBLE_FASTEST,
        );
      });

      sendTestBtn.addEventListener('click', sendTestMessage);

      testMessageInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          sendTestMessage();
        }
      });

      // Initialize
      window.addEventListener('load', async () => {
        await initGGWave();
        log('Ready to create or receive WebRTC connections', 'system');
        log(`Your client ID: ${clientId}`, 'system');
      });
    